import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { packetPdfBase64, type PacketDayRow } from '../weather/weatherPdf';

export interface WeatherObservation {
    obs_date: string;
    precip_in: number | null;
    temp_max_f: number | null;
    temp_min_f: number | null;
    source: string;
    station_id: string | null;
    station_name: string | null;
}

/**
 * The NOAA station the lot's station column actually came from. Null on lots that
 * have not been pulled since per-site station selection shipped, which is why the
 * packet renderers treat it as optional rather than assuming a station exists.
 */
export interface NoaaStation {
    id: string | null;
    name: string | null;
    distance_mi: number | null;
}

export interface WeatherPacket {
    job_id: string;
    lot_number: string;
    community: string;
    builder: string;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
    prep_date: string | null;
    pour_date: string | null;
    noaa_station: NoaaStation | null;
    observations: WeatherObservation[];
}

// get_weather_packet RPC: prep-to-pour window + all stored observations.
export function useWeatherPacket(jobId: string | null) {
    return useQuery({
        queryKey: ['weatherPacket', jobId],
        enabled: !!jobId,
        queryFn: async (): Promise<WeatherPacket> => {
            const { data, error } = await supabase.rpc('get_weather_packet', { p_job_id: jobId });
            if (error) throw error;
            return data as WeatherPacket;
        },
    });
}

// weather-pull Edge Function: geocode + Open-Meteo pull + nearest-station NOAA pull + upsert.
export function useWeatherPull() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (jobId: string) => {
            const { data, error } = await supabase.functions.invoke('weather-pull', {
                body: { job_id: jobId },
            });
            if (error) {
                // Surface the function's own error body when present
                let detail = error.message;
                try {
                    const ctx = (error as { context?: Response }).context;
                    if (ctx && typeof ctx.json === 'function') {
                        const body = await ctx.json();
                        if (body?.error) detail = body.error;
                    }
                } catch (_) {
                    // keep the generic message
                }
                throw new Error(detail);
            }
            return data;
        },
        onSuccess: (_data, jobId) => {
            qc.invalidateQueries({ queryKey: ['weatherPacket', jobId] });
        },
    });
}

export interface CompanyCamPushResult {
    matched: boolean;
    uploaded?: boolean;
    project?: { id: string; name: string; url: string };
    error?: string;
}

// companycam-push Edge Function: builds the packet PDF client-side (reusing
// packetPdfBase64), then hands the base64 to the function, which finds the
// lot's CompanyCam project by community + lot and uploads the PDF to it.
export function useCompanyCamPush() {
    return useMutation({
        mutationFn: async ({
            packet,
            rows,
        }: {
            packet: WeatherPacket;
            rows: PacketDayRow[];
        }): Promise<CompanyCamPushResult> => {
            const pdf_base64 = await packetPdfBase64(packet, rows);
            const { data, error } = await supabase.functions.invoke('companycam-push', {
                body: {
                    job_id: packet.job_id,
                    community: packet.community,
                    lot_number: packet.lot_number,
                    pdf_base64,
                },
            });
            if (error) {
                let detail = error.message;
                try {
                    const ctx = (error as { context?: Response }).context;
                    if (ctx && typeof ctx.json === 'function') {
                        const body = await ctx.json();
                        if (body?.error) detail = body.error;
                    }
                } catch (_) {
                    // keep generic message
                }
                throw new Error(detail);
            }
            return data as CompanyCamPushResult;
        },
    });
}
